import {
  BLUEPRINT_STOREY_LEVEL_SELECT_MAX,
  blueprintStoreyAssignmentFromStartLevel,
} from '@shared/helpers/design/blueprint-underlay.helpers';
import type { BlueprintStoreyAssignment } from '@shared/models/design/blueprint-underlay.model';

export type PdfImportPageDraft = {
  selected: boolean;
  assignment: BlueprintStoreyAssignment;
};

export type PdfImportPageDrafts = Record<number, PdfImportPageDraft>;

export type PdfImportWizardStep = 1 | 2;

/**
 * `inherit` — copy an already calibrated sheet's scale.
 * `measure` — place unscaled; the Design map takes over to measure it.
 */
export type PdfImportPlaceMode = 'inherit' | 'measure';

/** On-map measure hint — the one place that describes how to set a scale. */
export const BLUEPRINT_MEASURE_HINT =
  'Click two points and enter the real distance';

export type PdfImportWizardStepCopy = {
  title: string;
  subtitle: string;
};

/**
 * Wizard header copy — title + subtitle per step (no step numbers).
 */
export const PDF_IMPORT_WIZARD_STEP_COPY: Record<
  PdfImportWizardStep,
  PdfImportWizardStepCopy
> = {
  1: {
    title: 'Select pages',
    subtitle: 'Select the pages you want to use',
  },
  2: {
    title: 'Assign pages to storeys',
    subtitle:
      'Choose where each selected PDF page should appear in the project.',
  },
};

/**
 * Start-storey for a page ordinal among the selection, from a base level.
 * Page 0 → base, page 1 → base+1, capped at the project's storey count
 * ({@link maxLevel}).
 */
export const startLevelAssignmentForOrdinal = (
  ordinalAmongSelected: number,
  baseStartLevel: number,
  maxLevel: number = BLUEPRINT_STOREY_LEVEL_SELECT_MAX,
): BlueprintStoreyAssignment => {
  const level = Math.min(
    Math.max(1, Math.floor(maxLevel) || 1),
    Math.max(1, Math.floor(baseStartLevel) + ordinalAmongSelected),
  );
  return blueprintStoreyAssignmentFromStartLevel(level);
};

/**
 * Apply storey defaults for the current selection.
 * One page → {@link singlePageAssignment}; multiple → base, base+1, … by page order.
 */
export const withSelectionStoreyDefaults = (
  drafts: PdfImportPageDrafts,
  selectedPageIndices: readonly number[],
  singlePageAssignment: BlueprintStoreyAssignment,
  baseStartLevel: number,
  maxLevel: number = BLUEPRINT_STOREY_LEVEL_SELECT_MAX,
): PdfImportPageDrafts => {
  const sorted = [...selectedPageIndices].sort((a, b) => a - b);
  const selectedSet = new Set(sorted);
  const next: PdfImportPageDrafts = {};

  for (const [key, draft] of Object.entries(drafts)) {
    const pageIndex = Number(key);
    if (selectedSet.has(pageIndex)) continue;
    next[pageIndex] = { ...draft, selected: false };
  }

  const only = sorted.length === 1 ? sorted[0] : undefined;
  if (only !== undefined) {
    next[only] = {
      selected: true,
      assignment: singlePageAssignment,
    };
    return next;
  }

  sorted.forEach((pageIndex, ordinal) => {
    next[pageIndex] = {
      selected: true,
      assignment: startLevelAssignmentForOrdinal(
        ordinal,
        baseStartLevel,
        maxLevel,
      ),
    };
  });
  return next;
};

/**
 * True when a selected page has a placeable assignment.
 */
export const isAssignmentReady = (
  assignment: BlueprintStoreyAssignment,
): boolean => {
  if (assignment.kind !== 'otherStoreys') return true;
  return assignment.levels.length > 0;
};

export type PdfImportWizardPlan = {
  showSelectStep: boolean;
  showAssignStep: boolean;
  showWizard: boolean;
};

/**
 * Which import-wizard steps to show. Driven by selected page count, not the
 * PDF's total page count — except Select pages, which only exists when the
 * file has more than one page.
 * Assign storeys is skipped when the project has a single storey (no choice),
 * unless the design has several buildings to choose between.
 */
export const resolvePdfImportWizardPlan = (options: {
  pdfPageCount: number;
  selectedPageCount: number;
  storeyLevelCount: number;
  /** Buildings in the design. Several always ask which one the pages are for. */
  buildingCount?: number;
}): PdfImportWizardPlan => {
  const showSelectStep = options.pdfPageCount > 1;
  const showAssignStep =
    (options.selectedPageCount > 0 && (options.buildingCount ?? 1) > 1) ||
    (options.selectedPageCount > 1 && options.storeyLevelCount > 1);
  return {
    showSelectStep,
    showAssignStep,
    showWizard: showSelectStep || showAssignStep,
  };
};
